import React, { useState, useEffect } from 'react';
import { authFetch } from '@/utils/auth-fetch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { AlertCircle, Settings, RefreshCw, Check, X } from 'lucide-react';

interface FeedField {
  name: string;
  productCount: number;
  uniqueValues: number;
  source: string;
}

interface EnrichmentSettings {
  excludedFields: string[];
  preferredAttributes: string[];
  autoEnrichNewProducts: boolean;
}

interface EnrichmentSettingsModalProps {
  siteId: string;
  isOpen: boolean;
  onClose: () => void;
  onSave?: (settings: EnrichmentSettings) => void;
}

export function EnrichmentSettingsModal({
  siteId,
  isOpen,
  onClose,
  onSave
}: EnrichmentSettingsModalProps) {
  const [fields, setFields] = useState<FeedField[]>([]);
  const [settings, setSettings] = useState<EnrichmentSettings>({
    excludedFields: [],
    preferredAttributes: [],
    autoEnrichNewProducts: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && siteId) {
      loadData();
    }
  }, [isOpen, siteId]);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Load feed fields and enrichment settings in parallel
      const [fieldsRes, settingsRes] = await Promise.all([
        authFetch(`/api/sites/${siteId}/feed-fields`),
        authFetch(`/api/sites/${siteId}/enrichment-settings`)
      ]);

      if (fieldsRes.ok) {
        const fieldsData = await fieldsRes.json();
        setFields(fieldsData.fields || []);
      }

      if (settingsRes.ok) {
        const settingsData = await settingsRes.json();
        setSettings(settingsData.enrichmentSettings || {
          excludedFields: [],
          preferredAttributes: [],
          autoEnrichNewProducts: false
        });
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const toggleFieldExclusion = (fieldName: string) => {
    setSettings(prev => {
      const excluded = prev.excludedFields || [];
      const isExcluded = excluded.includes(fieldName);

      return {
        ...prev,
        excludedFields: isExcluded
          ? excluded.filter(f => f !== fieldName)
          : [...excluded, fieldName]
      };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      const response = await authFetch(`/api/sites/${siteId}/enrichment-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enrichmentSettings: settings })
      });

      if (!response.ok) {
        throw new Error('Failed to save settings');
      }

      onSave?.(settings);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const isExcluded = (fieldName: string) => {
    return (settings.excludedFields || []).includes(fieldName);
  };

  // Common fields that are usually safe to exclude
  const suggestedExclusions = ['weight', 'shipping', 'seller_tos', 'seller_url', 'return_policy', 'offer_id'];

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Enrichment Settings
          </DialogTitle>
          <DialogDescription>
            Configure which fields should be excluded from AI enrichment processing.
            Excluded fields will not be used as source data for attribute extraction.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-md">
            <AlertCircle className="h-4 w-4" />
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Suggested Exclusions */}
            <div className="p-3 bg-amber-50 rounded-md">
              <h4 className="text-sm font-medium text-amber-800 mb-2">
                Suggested Exclusions
              </h4>
              <p className="text-xs text-amber-700 mb-2">
                These fields often contain package/shipping info rather than product attributes:
              </p>
              <div className="flex flex-wrap gap-2">
                {suggestedExclusions
                  .filter(f => fields.some(field => field.name === f))
                  .map(fieldName => (
                    <Badge
                      key={fieldName}
                      variant={isExcluded(fieldName) ? 'default' : 'outline'}
                      className="cursor-pointer"
                      onClick={() => toggleFieldExclusion(fieldName)}
                    >
                      {isExcluded(fieldName) && <Check className="h-3 w-3 mr-1" />}
                      {fieldName}
                    </Badge>
                  ))}
              </div>
            </div>

            {/* All Fields */}
            <div>
              <h4 className="text-sm font-medium mb-2">
                All Feed Fields ({fields.length})
              </h4>
              <ScrollArea className="h-[300px] border rounded-md">
                <div className="p-2 space-y-1">
                  {fields.map(field => (
                    <div
                      key={field.name}
                      className={`flex items-center justify-between p-2 rounded hover:bg-gray-50 ${
                        isExcluded(field.name) ? 'bg-gray-100' : ''
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Checkbox
                          checked={isExcluded(field.name)}
                          onCheckedChange={() => toggleFieldExclusion(field.name)}
                        />
                        <div>
                          <span className={`font-mono text-sm ${isExcluded(field.name) ? 'text-gray-500 line-through' : ''}`}>
                            {field.name}
                          </span>
                          {isExcluded(field.name) && (
                            <Badge variant="secondary" className="ml-2 text-xs">
                              <X className="h-3 w-3 mr-1" />
                              Excluded
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span>{field.productCount.toLocaleString()} products</span>
                        <span className="text-gray-300">|</span>
                        <span>{field.uniqueValues.toLocaleString()} unique</span>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>

            {/* Summary */}
            {settings.excludedFields?.length > 0 && (
              <div className="p-3 bg-blue-50 rounded-md">
                <h4 className="text-sm font-medium text-blue-800 mb-1">
                  Exclusion Summary
                </h4>
                <p className="text-xs text-blue-700">
                  {settings.excludedFields.length} field(s) will be excluded from enrichment:
                  {' '}{settings.excludedFields.join(', ')}
                </p>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={loading || saving}>
            {saving ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Settings'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
